import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, Paper } from '@mui/material';
import { Clock, Check, CheckCheck, AlertCircle } from 'lucide-react';
import { io } from 'socket.io-client';
const socket = io(process.env.REACT_APP_API_URL);

const ReservationChat = ({ reservationId, guestEmail, propertyId, sender = "hotel" }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const messagesRef = useRef([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/messages/${reservationId}`)
      .then(res => res.json())
      .then(data => {
        const msgs = data.messages || [];
        setMessages(msgs);
        messagesRef.current = msgs;

        const unseen = msgs.filter(m => m.sender !== sender && !m.seen);
        unseen.forEach((msg) => {
          fetch(`${process.env.REACT_APP_API_URL}/api/messages/seen`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reservationId, messageId: msg._id })
          }).then(() => {
            socket.emit("messageSeen", { messageId: msg._id });
            setMessages((prev) =>
              prev.map((m) => m._id === msg._id ? { ...m, seen: true } : m)
            );
          }).catch(console.error);
        });
      });

    socket.emit("joinRoom", reservationId);
    socket.on("connect", () => {
      socket.emit("joinRoom", reservationId);
    });

    socket.on("newMessage", (msg) => {
      if (msg.reservationId !== reservationId) return;

      if (msg.sender !== sender) {
        msg.seen = true;
        fetch(`${process.env.REACT_APP_API_URL}/api/messages/seen`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reservationId: msg.reservationId, messageId: msg._id })
        }).then(() => {
          socket.emit("messageSeen", { messageId: msg._id, reservationId: msg.reservationId });
          setMessages((prev) =>
            prev.map((m) =>
              m._id === msg._id || m.tempId === msg.tempId ? { ...m, seen: true } : m
            )
          );
        }).catch(console.error);
      }

      setMessages((prev) => {
        const exists = prev.some((m) => m._id === msg._id || m.tempId === msg.tempId);
        if (exists) return prev;

        const newMsg = { ...msg, status: "delivered" };
        messagesRef.current = [...prev, newMsg];
        return [...prev, newMsg];
      });
    });

    socket.on("messageSeen", ({ messageId }) => {
      const updated = messagesRef.current.map((m) => {
        const isMatch = m._id === messageId || m.tempId === messageId;
        if (isMatch) {
          return { ...m, seen: true, status: m.status ?? "delivered" };
        }
        return m;
      });
      messagesRef.current = updated;
      setMessages(updated);
    });

    return () => {
      socket.off("newMessage");
      socket.off("messageSeen");
    };
  }, [reservationId, sender]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const tempId = `temp-${Math.random().toString(36).substring(2, 9)}`;
    const msg = {
      reservationId,
      sender,
      content: input,
      guestEmail,
      propertyId,
      tempId
    };

    const optimisticMsg = {
      ...msg,
      timestamp: new Date(),
      _id: tempId,
      status: "sending"
    };
    setMessages((prev) => {
      const updated = [...prev, optimisticMsg];
      messagesRef.current = updated;
      return updated;
    });
    setInput("");
    socket.emit("joinRoom", reservationId);

    fetch(`${process.env.REACT_APP_API_URL}/api/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(msg),
    }).catch(console.error);
  };

  return (
    <Box mt={1}>
      <Paper
        variant="outlined"
        sx={{
          maxHeight: 580,
          minHeight: 450,
          overflowY: "auto",
          mb: 2,
          p: 2,
          maxWidth: '100%',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}
      >
        {messages.map((msg, i) => (
          <Box key={i} textAlign={msg.sender === sender ? "right" : "left"}>
            <Box
              sx={{
                display: "inline-block",
                bgcolor: msg.sender === sender ? "#e6f4ea" : "#f0f0f0",
                borderRadius: 3,
                px: 2,
                py: 1.2,
                my: 1.2,
                maxWidth: "85%",
                fontSize: "1rem",
                lineHeight: 1.6,
              }}
            >
              <Typography variant="body2">
                {msg.content?.trim() ? msg.content : ""}
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  {dayjs(msg.timestamp).fromNow()}
                </Typography>
                {msg.status === "sending" && <Clock size={14} style={{ marginLeft: 6 }} />}
                {msg.status === "delivered" && !msg.seen && <Check size={14} style={{ marginLeft: 6 }} />}
                {msg.seen && <CheckCheck size={14} style={{ marginLeft: 6 }} />}
                {msg.status === "failed" && <AlertCircle size={14} style={{ marginLeft: 6 }} />}
              </Box>
            </Box>
          </Box>
        ))}
        <div ref={bottomRef} />
      </Paper>

      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <Button
          variant="contained"
          onClick={sendMessage}
          disabled={!input.trim()}
          sx={{ height: '100%' }}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
};

export default ReservationChat;